
/**
 * Where the money goes, made simple (Operations).
 *
 *   moneyFlowHtml(flow)   flow = /api/operations/desk money_flow (last month's income statement)
 *
 * One picture of the month (the "Every $1 of revenue" strip was removed, 2026-10-15):
 *   A flow diagram: revenue on the left pours into each cost on the right;
 *      what's left is profit. If costs beat revenue, the gap flows in from
 *      your cash ("Paid from cash") so both sides always add up.
 * Pure (data in, HTML out), so it's tested in Node.
 */
const mfEsc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** "$1,234" or, compact, "$1.2M". */
function mfMoney(v, { compact = true } = {}) { // short by default (2.7)
  const a = Math.abs(v || 0), sign = v < 0 ? "-" : "";
  if (compact && a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (compact && a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (compact && a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${Math.round(a).toLocaleString("en-US")}`;
}

const MF_LINES = [
  ["cogs", "Making what you sell", "#f6b26b", "Materials, hosting, delivery"],
  ["gna", "Staff & running the company", "#a78bfa", "Salaries, offices, admin"],
  ["snm", "Sales & marketing", "#f472b6", "Ads, sales team, campaigns"],
  ["rnd", "Research & development", "#7ee8fa", "Engineers building what's next"],
  ["depreciation", "Equipment wearing out", "#8b93ad", "Spread-out cost of what you bought"],
  ["interest", "Interest to lenders", "#fbbf24", "The cost of your loans"],
  ["tax", "Tax", "#9fb4ff", "To the government"],
];

function moneyFlowHtml(f) {
  const rev = Math.max(0, f.revenue || 0);
  const costs = MF_LINES.map(([k, label, color, what]) => ({ k, label, color, what, v: Math.max(0, f[k] || 0) })).filter((c) => c.v >= 0.5);
  const totalCost = costs.reduce((s, c) => s + c.v, 0);
  const profit = rev - totalCost;
  if (rev < 0.5 && totalCost < 0.5) return `<p class="mf-empty">No money has moved yet. Play a month and this shows where every dollar went.</p>`;

  // (the "Every $1 of revenue" strip was removed at Rachit's request, 2026-10-15: the flow diagram says it)

  // 2. the flow diagram
  const W = 760, H = 330, padT = 16, gap = 8;
  const leftTotal = Math.max(rev, totalCost), right = [...costs];
  if (profit > 0) right.push({ k: "profit", label: "Profit you keep", color: "#34d399", what: "What's left after everything", v: profit });
  const left = [{ k: "revenue", label: "Revenue", color: "#34d399", v: rev }];
  if (profit < 0) left.push({ k: "cash", label: "Paid from cash", color: "#fb7185", v: -profit });
  const usable = H - padT * 2 - gap * (right.length - 1);
  const scale = usable / Math.max(1, leftTotal);
  const xL = 150, xR = W - 250, nodeW = 14;
  let y = padT;
  const lNodes = left.map((n) => { const h = Math.max(2, n.v * scale); const o = { ...n, y, h }; y += h + gap * 2; return o; });
  y = padT;
  const rNodes = right.map((n) => { const h = Math.max(2, n.v * scale); const o = { ...n, y, h }; y += h + gap; return o; });
  // ribbons: fill each right node from the left nodes in order (revenue first, then cash)
  const ribbons = [];
  let li = 0, lUsed = 0;
  for (const r of rNodes) {
    let need = r.v, rUsed = 0;
    while (need > 0.01 && li < lNodes.length) {
      const l = lNodes[li], take = Math.min(need, l.v - lUsed);
      if (take > 0.01) {
        ribbons.push({ y0: l.y + lUsed * scale, y1: r.y + rUsed * scale, h: take * scale, color: l.k === "cash" ? "#fb7185" : r.color, title: `${l.label} → ${r.label}: ${mfMoney(take)}` });
        lUsed += take; rUsed += take; need -= take;
      }
      if (l.v - lUsed <= 0.01) { li++; lUsed = 0; }
    }
  }
  const curve = (b) => {
    const x0 = xL + nodeW, x1 = xR, mx = (x0 + x1) / 2;
    return `M${x0},${b.y0} C${mx},${b.y0} ${mx},${b.y1} ${x1},${b.y1} L${x1},${b.y1 + b.h} C${mx},${b.y1 + b.h} ${mx},${b.y0 + b.h} ${x0},${b.y0 + b.h} Z`;
  };
  const pctOf = (v) => (rev ? `${Math.round((100 * v) / rev)}%` : "");
  // right-hand labels: at least 17px apart, so tiny costs don't print on top of each other
  let lastY = -1e9;
  for (const n of rNodes) { n.ly = Math.max(n.y + n.h / 2 + 4, lastY + 17); lastY = n.ly; }
  const svg = `<svg class="mf-svg" viewBox="0 0 ${W} ${H}" role="img" aria-label="Where last month's money went">
    ${ribbons.map((b, i) => `<path d="${curve(b)}" fill="${b.color}" fill-opacity=".32" class="mf-rib" style="animation-delay:${i * 70}ms"><title>${mfEsc(b.title)}</title></path>`).join("")}
    ${lNodes.map((n) => `<rect x="${xL}" y="${n.y.toFixed(1)}" width="${nodeW}" height="${n.h.toFixed(1)}" rx="3" fill="${n.color}"/>
      <text x="${xL - 10}" y="${(n.y + n.h / 2 - 4).toFixed(1)}" text-anchor="end" class="mf-l">${mfEsc(n.label)}</text>
      <text x="${xL - 10}" y="${(n.y + n.h / 2 + 12).toFixed(1)}" text-anchor="end" class="mf-v">${mfMoney(n.v, { compact: true })}</text>`).join("")}
    ${rNodes.map((n) => `<g><title>${mfEsc(n.label)}: ${mfEsc(n.what)}</title><rect x="${xR}" y="${n.y.toFixed(1)}" width="${nodeW}" height="${n.h.toFixed(1)}" rx="3" fill="${n.color}"/>
      ${Math.abs(n.ly - (n.y + n.h / 2 + 4)) > 2 ? `<path d="M${xR + nodeW + 2},${(n.y + n.h / 2).toFixed(1)} L${xR + nodeW + 8},${(n.ly - 4).toFixed(1)}" stroke="${n.color}" stroke-width="1" fill="none"/>` : ""}
      <text x="${xR + nodeW + 10}" y="${n.ly.toFixed(1)}" class="mf-l">${mfEsc(n.label)} <tspan class="mf-v">${mfMoney(n.v, { compact: true })}</tspan> <tspan class="mf-p">${pctOf(n.v)}</tspan></text></g>`).join("")}
  </svg>`;
  const verdict = profit >= 0
    ? `<p class="mf-verdict pos">You kept <b>${mfMoney(profit, { compact: true })}</b> of <b>${mfMoney(rev, { compact: true })}</b> revenue (${pctOf(profit)}).</p>`
    : `<p class="mf-verdict neg">Costs were <b>${mfMoney(totalCost, { compact: true })}</b> against <b>${mfMoney(rev, { compact: true })}</b> of revenue: <b>${mfMoney(-profit, { compact: true })}</b> came out of your cash.</p>`;
  return `<div class="mf">${svg}${verdict}</div>`;
}

export { moneyFlowHtml };
